"use strict";
/*
 * Integration App React package
 * {@link https://www.npmjs.com/package/@integration-app/react}
 * v2.23.0
 */
'use client';

import { jsx } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import { clsx } from 'clsx';
import '../../styled-system/css/css.mjs';
import '../../styled-system/helpers.mjs';
import { sva } from '../../styled-system/css/sva.mjs';
import { SvgIcon } from '../SvgIcon/index.mjs';

const dataBuilderCellRecipe = sva({
  className: "data-builder-cell",
  slots: ["root"],
  base: {
    root: {
      overflowX: "hidden",
      display: "flex",
      flexDir: "column",
      alignItems: "start",
      justifyContent: "center",
      borderLeftWidth: "1px",
      borderLeftColor: "slate.3",
      px: "2.5",
      py: "1.5",
      minH: "8.5",
      bg: "white",
      borderWidth: "1px 1px 1px 0",
      borderColor: "slate.3",
      _first: {
        borderLeftWidth: "1px",
        borderBottomLeftRadius: "token(sizes.2.5)",
        borderTopLeftRadius: "token(sizes.2.5)"
      },
      _last: {
        borderBottomRightRadius: "token(sizes.2.5)",
        borderTopRightRadius: "token(sizes.2.5)"
      }
    }
  },
  variants: {
    type: {
      name: {
        root: {
          flexGrow: "0",
          flexShrink: "0",
          flexWrap: "nowrap",
          alignSelf: "stretch",
          minW: "40"
        }
      },
      combobox: {
        root: {
          flexDir: "row",
          flexWrap: "wrap",
          justifyContent: "start",
          alignItems: "center",
          px: "0",
          py: "0"
        }
      },
      input: {
        root: {
          px: "2",
          py: "2",
          "& > input": {
            w: "full"
          }
        }
      },
      value: {
        root: {
          py: "2"
        }
      },
      icon: {
        root: {
          flexGrow: "0",
          flexShrink: "0",
          flexWrap: "nowrap",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "neutral.10",
          px: "2.5",
          py: "2.5",
          "& svg": {
            w: "3.5",
            h: "3.5"
          }
        }
      }
    },
    disabled: {
      true: {
        root: {
          pointerEvents: "none",
          bg: "slate.1"
        }
      },
      false: {}
    },
    span: {
      shrink: {
        root: {
          flexGrow: "0",
          flexShrink: "1"
        }
      },
      grow: {
        root: {
          flexGrow: "1"
        }
      }
    }
  }
});
const Cell = forwardRef(
  ({ children, disabled, className, type, span, icon, ...props }, ref) => {
    const styles = dataBuilderCellRecipe({ type, disabled, span });
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: clsx(styles.root, className),
        "aria-disabled": disabled,
        "aria-readonly": disabled,
        ...props,
        ref,
        children: icon ? /* @__PURE__ */ jsx(SvgIcon, { type: icon }) : children
      }
    );
  }
);
Cell.displayName = "Cell";
const dataBuilderCellButtonRecipe = sva({
  className: "data-builder-cell-button",
  slots: ["root"],
  base: {
    root: {
      cursor: "pointer",
      _hover: {
        bg: "slate.2"
      },
      _active: {
        bg: "slate.3"
      }
    }
  }
});
const CellButton = forwardRef(({ children, disabled, className, type, icon, ...props }, ref) => {
  const styles = dataBuilderCellRecipe({ type, disabled });
  const buttonStyles = dataBuilderCellButtonRecipe();
  return /* @__PURE__ */ jsx(
    "button",
    {
      className: clsx(styles.root, buttonStyles.root, className),
      "aria-disabled": disabled,
      "aria-readonly": disabled,
      ...props,
      ref,
      children: icon ? /* @__PURE__ */ jsx(SvgIcon, { type: icon }) : children
    }
  );
});
CellButton.displayName = "CellButton";

export { Cell, CellButton, dataBuilderCellButtonRecipe, dataBuilderCellRecipe };
